import { TrainingLinksInput } from "../../components/learning/TrainingLinksInput";
import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import {
  useCreateCourse,
  useUpdateCourse,
} from "../../backend/queries/learning";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

export function CreateCourseDialog({
  course,
}: {
  course?: {
    id: string;
    title: string;
    description: string;
    content_url?: string | null;
    content_urls?: string[];
    pass_percent: number;
  };
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(course?.title || "");
  const [urls, setUrls] = useState(
    course?.content_urls?.length
      ? course.content_urls
      : [course?.content_url || ""],
  );
  const [description, setDescription] = useState(course?.description || "");
  const [error, setError] = useState("");
  const create = useCreateCourse();
  const update = useUpdateCourse();
  const pending = create.isPending || update.isPending;
  const linked = !course || Boolean(course.content_url);
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      const payload = {
        title: title.trim(),
        content_urls: linked ? urls.map((url) => url.trim()) : [],
        description: description.trim(),
        pass_percent: course?.pass_percent || 80,
      };
      if (course) await update.mutateAsync({ id: course.id, payload });
      else await create.mutateAsync(payload);
      setOpen(false);
      if (!course) {
        setTitle("");
        setUrls([""]);
        setDescription("");
      }
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not create the course. Your entries are kept so you can try again.",
      );
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={course ? "outline" : "default"}>
          {course ? (
            <Pencil className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {course ? "Edit course" : "Add course"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{course ? "Edit course" : "Add a course"}</DialogTitle>
          <DialogDescription>
            {course
              ? "Update content. Existing completions are kept."
              : "Add training links. Publish when ready."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-5">
          <div className="grid gap-2">
            <Label htmlFor="course-title">Course title</Label>
            <Input
              id="course-title"
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g. New starter induction"
            />
          </div>
          {linked && <TrainingLinksInput value={urls} onChange={setUrls} />}
          <div className="grid gap-2">
            <Label htmlFor="course-description">
              Instructions{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <Textarea
              id="course-description"
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What should your team know before they start?"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                pending ||
                !title.trim() ||
                (linked && urls.some((url) => !url.trim()))
              }
            >
              {pending ? "Saving…" : course ? "Save changes" : "Save draft"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
